import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const NextBtn = styled.button`
  padding: 0.625rem 1.5rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  color: white;
  background: #22c55e;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  &:hover { background: #16a34a; }
`;

const STEPS = [
  {
    emoji: '🎉',
    title: 'No Listing Fees',
    body: 'No listing fees on any category — list anything for free.',
  },
  {
    emoji: '💰',
    title: '5% Commission Only When Sold',
    body: 'Pay a 5% commission from the item price only when it gets sold. Applies on all categories except Pre-owned Curated Finds.',
  },
  {
    emoji: '🏪',
    title: 'Sell Your Way',
    body: 'Sell pre-owned items, open a boutique, or set up as a designer — same low commission across all categories.',
  },
];

const STORAGE_KEY = 'flea_web_first_signin_shown';

function isShown() {
  try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
}

function markShown() {
  try { localStorage.setItem(STORAGE_KEY, 'true'); } catch {}
}

export default function FirstSigninPopup({ onComplete }) {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setOpen(false);
      return;
    }
    if (isShown()) return;
    const id = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(id);
  }, [isAuthenticated, user]);

  const handleSkip = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      markShown();
      setOpen(false);
      onComplete?.();
    }
  }, [step, onComplete]);

  const handleSkipAll = useCallback(() => {
    markShown();
    setOpen(false);
    onComplete?.();
  }, [onComplete]);

  if (!open) return null;

  const current = STEPS[step];

  return (
    <div
      className="fixed inset-0 z-[14000] flex items-center justify-center bg-black/50 px-4 py-8"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
        style={{ backgroundColor: '#f0fdf4' }}
      >
        <div className="p-8 text-center">
          <div className="text-6xl mb-5" aria-hidden>{current.emoji}</div>
          <h2 className="text-xl font-bold text-gray-800 mb-3">{current.title}</h2>
          <p className="text-gray-600 text-sm leading-relaxed">{current.body}</p>

          <div className="flex justify-center gap-2 mt-7">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-emerald-400' : 'w-2 bg-emerald-200'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 pb-6">
          <button
            type="button"
            onClick={handleSkipAll}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors font-medium"
          >
            Skip all
          </button>
          <NextBtn type="button" onClick={handleSkip}>
            {step < STEPS.length - 1 ? 'Skip' : 'Done'}
          </NextBtn>
        </div>
      </div>
    </div>
  );
}
